// src/modules/leads/LeadDetails.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import DashboardLayout from '../../layouts/DashboardLayout';
import CommunicationHistory from '../../components/communications/CommunicationHistory';
import CallModal from '../../components/shared/CallModal';
import {
  getLeadWithRelated,
  getLeadJobs,
  getLeadEstimates,
  getLeadStatuses,
  updateLeadStatus,
  updateLeadPipeline,
  getLeadNotes,
  createLeadNote,
  updateLeadNote,
  deleteLeadNote
} from './leadService';
import { 
  FaUser, 
  FaPhoneAlt, 
  FaEdit, 
  FaSms, 
  FaEye, 
  FaPaperPlane,
  FaCalendarAlt,
  FaArrowLeft,
  FaCheck,
  FaPen,
  FaList,
  FaInfo,
  FaFileAlt,
  FaTractor,
  FaPlus,
  FaHistory, FaComment, FaGlobeAmericas, FaSave, FaTrash, FaShareAlt, FaFileInvoiceDollar, FaSatellite,
  FaClock, FaEnvelopeOpen, FaSignature, FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import Modal from '../../components/shared/Modal';
import CollapsibleSection from '../../components/shared/CollapsibleSection';
import FeedbackModal from '../../components/shared/ModalNotification';
import { updateEstimateStatus } from '../estimates/EstimateService';
import { ChangeOrderSummaryBadge } from '../change-orders/ChangeOrderListSection';
import SmsModal from './SmsModal';
import EmailModal from './EmailModal';
import LeadAddressMap from './LeadAddressMap';
import LeadScheduledCalls from '../call-scheduling/components/LeadScheduledCalls';
import LeadScheduledVisits from '../routes/components/LeadScheduledVisits';
import AddToRouteButton from '../routes/components/AddToRouteButton';
import { getDirectCallKey, formatPhoneNumber } from '../../services/communicationService';
import { getLeadAddresses, createLeadAddress, updateLeadAddress, deleteLeadAddress } from './leadAddressService';
import { getLeadPhoneNumbers } from './leadPhoneService';
import { getLeadEmails, createLeadEmail, updateLeadEmail, deleteLeadEmail } from './leadEmailService';
import SendEstimateModal from '../estimates/SendEstimateModal';
import MergeLeadModal from './MergeLeadModal';
import LeadSubmissions, { SubmissionThumb, ImageViewer } from './LeadSubmissions';
import { getLeadSubmissions } from './leadSubmissionService';
import GooglePlaceAutocomplete from '../../components/shared/GooglePlaceAutocomplete';
import Input from '../../components/shared/Input';
import Button from '../../components/shared/Button';
import { formatCurrency, formatDate } from '../../utils/utils';
import { formatAddress } from '../../utils/estimateAddressUtils';
import { 
  DetailContainer, 
  DetailHeader, 
  DetailTitle,
  DetailSection,
  DetailGrid,
  DetailField
} from '../../components/styled/DetailComponents';
import Select from '../../components/shared/Select';
import { useAuth } from '../auth/AuthContext';  // Import the auth context
import WeatherForecast from '../../components/weather/WeatherForecast';
import OriginBadge from '../../components/shared/OriginBadge';
import PreferredContactBadge, { preferredContactLabel } from '../../components/shared/PreferredContactBadge';
import { FaEnvelope, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaSeedling, FaCopy, FaDirections, FaLink, FaListUl } from 'react-icons/fa';

const ContactInfoBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing(1)};
  width: 100%;
  margin-top: ${p => p.theme.spacing(1.5)};
  padding-top: ${p => p.theme.spacing(1.5)};
  border-top: 1px solid ${p => p.theme.colors.inputGray}22;
`;

const CopyChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${p => p.$copied ? p.theme.colors.success + '22' : p.theme.colors.background};
  border: 1px solid ${p => p.$copied ? p.theme.colors.success + '55' : 'transparent'};
  border-radius: 20px;
  color: ${p => p.$copied ? p.theme.colors.success : p.theme.colors.textPrimary};
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;

  .chip-icon {
    color: ${p => p.$copied ? p.theme.colors.success : p.theme.colors.primary};
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  .copy-icon {
    color: ${p => p.$copied ? p.theme.colors.success : p.theme.colors.inputGray};
    font-size: 0.65rem;
    flex-shrink: 0;
    opacity: 0;
    transition: opacity 0.15s;
  }

  &:hover {
    background: ${p => p.theme.colors.surface};
    .copy-icon { opacity: 1; }
  }

  @media (max-width: 768px) {
    font-size: 0.78rem;
    padding: 5px 10px;
    .copy-icon { opacity: 0.6; }
  }
`;

const MetaTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: ${p => p.theme.colors.textSecondary};
  background: ${p => p.theme.colors.background};
  padding: 5px 10px;
  border-radius: 16px;

  svg {
    font-size: 0.7rem;
    color: ${p => p.color || p.theme.colors.inputGray};
  }
`;

const EstimateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${props => props.theme.spacing(3)};
  margin-top: ${props => props.theme.spacing(3)};
`;

// Same mapping the public quote page uses, so a rep and a customer see the
// same unit label for a line.
const UNIT_LABELS = { sqft: 'sq ft', lnft: 'lin ft', yd: 'yd', unit: 'unit' };
const formatQty = (item) => {
  const n = Number(item.totalArea);
  const qty = Number.isFinite(n) ? n.toLocaleString('en-US') : item.totalArea;
  return `${qty} ${UNIT_LABELS[item.measurementUnit] || item.measurementUnit || 'sq ft'}`;
};

const EstimateCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${props => props.theme.spacing(2)};
    gap: 20px !important;
  }

  .price {
    font-size: ${props => props.theme.typography.fontSize.large};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    color: ${props => props.theme.colors.success};
  }

  .actions {
    display: flex;
    gap: ${props => props.theme.spacing(2)};
    margin-top: ${props => props.theme.spacing(2)};
  }
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  border-radius: 12px;
  font-size: ${props => props.theme.typography.fontSize.small};
  background: ${props => props.color}22;
  color: ${props => props.color};
`;

const ItemListWrapper = styled.div`
padding: 20px;
background-color: ${(props) => props.theme.colors.surface};
border-radius: 12px;
box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
margin-top: ${(props) => props.theme.spacing(4)};
`;
const ItemListLink = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px;
  border-bottom: 1px solid ${(props) => props.theme.colors.inputGray};
  margin-bottom: ${(props) => props.theme.spacing(2)};
  cursor: pointer !important;

  &:hover{
    background-color: #2e2d2d;
  }

  .header {
    display: flex;
    justify-content: space-between; /* Add space between supplier name and distance */
    width: 100%; /* Ensure the header takes full width */
    align-items: center;
    gap: 20px;
  }

  .item-name {
    font-size: 18px;
    font-weight: bold;
  }
  
  .right-item {
    display: flex;
    font-size: 16px;
    font-weight: bold;
    color: ${(props) => props.theme.colors.primaryText};
  }

  .item-list {
    cursor: pointer;
    margin-top: ${(props) => props.theme.spacing(1)};
    font-size: 14px;
    color: ${(props) => props.theme.colors.textSecondary};
  }
  .price {
    font-size: ${props => props.theme.typography.fontSize.large};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    color: ${props => props.theme.colors.success};
  }

  .actions {
    display: flex;

    padding: 0px;margin: 0px;
    gap: ${props => props.theme.spacing(5)};
    margin-top: ${props => props.theme.spacing(0)};
  }

  button{
  padding: 0px;
  margin-top: 5px;
  }
`;

const EstimateActivityToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 0.78rem;
  cursor: pointer;
  padding: 6px 0 0 0;
  &:hover { color: ${p => p.theme.colors.primary}; }
`;

// Fast preview of an estimate's line items — "how much sealcoat is in this
// quote?" without opening the quote.
const ItemPreview = styled.div`
  margin-top: 10px;
  padding: 10px 14px;
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius};
  border-left: 3px solid ${p => p.theme.colors.primary};
`;

const ItemRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 5px 0;
  font-size: 0.82rem;

  & + & { border-top: 1px solid ${p => p.theme.colors.inputGray}1A; }

  .svc {
    flex: 1;
    min-width: 0;
    color: ${p => p.theme.colors.textPrimary};
    font-weight: 500;
    word-break: break-word;
  }
  .qty {
    flex-shrink: 0;
    color: ${p => p.theme.colors.primary};
    font-weight: 600;
    white-space: nowrap;
  }
  .amt {
    flex-shrink: 0;
    color: ${p => p.theme.colors.textSecondary};
    white-space: nowrap;
    min-width: 68px;
    text-align: right;
  }
`;

const ActivityLog = styled.div`
  margin-top: 10px;
  padding: 12px 14px;
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius};
  border-left: 3px solid ${p => p.theme.colors.primary};
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-size: 0.82rem;
  color: ${p => p.theme.colors.textSecondary};
  border-bottom: 1px solid ${p => p.theme.colors.inputGray}18;

  &:last-child { border-bottom: none; }

  .activity-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    flex-shrink: 0;
    font-size: 11px;
  }

  .activity-text {
    flex: 1;
    color: ${p => p.theme.colors.textPrimary};
    font-weight: 500;
  }

  .activity-date {
    white-space: nowrap;
    font-size: 0.78rem;
  }
`;

const EstimateAddrTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  color: ${p => p.theme.colors.textSecondary};
  padding: 4px 0;

  svg { color: ${p => p.theme.colors.primary}; flex-shrink: 0; }
`;

const AddressCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing(1.5)};
  padding: ${p => p.theme.spacing(2)};
  border-radius: ${p => p.theme.borderRadius};
  border: 1px solid ${p => p.$primary ? p.theme.colors.primary + '44' : p.theme.colors.inputGray + '33'};
  background: ${p => p.$primary ? p.theme.colors.primary + '0d' : p.theme.colors.background};
  transition: all 0.2s ease;

  .addr-badge {
    font-size: 0.7rem;
    font-weight: ${p => p.theme.typography.fontWeight.bold};
    color: ${p => p.theme.colors.primary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }

  .addr-label {
    font-weight: ${p => p.theme.typography.fontWeight.medium};
    color: ${p => p.theme.colors.textPrimary};
    margin-right: ${p => p.theme.spacing(1)};
  }

  .addr-text {
    flex: 1;
    color: ${p => p.theme.colors.textPrimary};
    font-size: ${p => p.theme.typography.fontSize.small};
    min-width: 0;
  }

`;

const AddrActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing(1)};
  flex-shrink: 0;

  .addr-action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: ${p => p.theme.colors.surface};
    color: ${p => p.theme.colors.textSecondary};
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    padding: 0;
    font-size: 14px;

    &:hover {
      background: ${p => p.theme.colors.primary}22;
      color: ${p => p.theme.colors.primary};
    }
    &.delete:hover {
      background: ${p => p.theme.colors.error}22;
      color: ${p => p.theme.colors.error};
    }
  }
`;

const AddrModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing(2)};
  margin-top: ${p => p.theme.spacing(1)};
`;

// Pairs two cards side by side on desktop and stacks them on phones.
// The cards bring their own vertical margin, so the grid only has to supply
// the horizontal gutter — hence gap: 0 once stacked.
const SectionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 ${p => p.theme.spacing(2)};
  align-items: start;

  /* Without this, long addresses and email addresses force a grid column
     wider than its share and push the layout sideways. */
  > * {
    min-width: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const Hint = styled.p`
  margin: ${p => p.theme.spacing(1)} 0 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: ${p => p.theme.colors?.textSecondary || '#6b7280'};
`;

const FieldError = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${p => p.theme.colors?.danger || '#e74c3c'};
`;

const AddAddressBtn = styled.button`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing(1)};
  padding: ${p => p.theme.spacing(1.5)} ${p => p.theme.spacing(2)};
  border: 1px dashed ${p => p.theme.colors.inputGray + '66'};
  border-radius: ${p => p.theme.borderRadius};
  background: transparent;
  color: ${p => p.theme.colors.textSecondary};
  cursor: pointer;
  font-size: ${p => p.theme.typography.fontSize.small};
  transition: all 0.2s ease;
  align-self: flex-start;
  font-family: inherit;
  &:hover {
    border-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const NewAddrFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing(1.5)};
  padding: ${p => p.theme.spacing(2.5)};
  border: 1px solid ${p => p.theme.colors.inputGray + '33'};
  border-radius: ${p => p.theme.borderRadius};
  background: ${p => p.theme.colors.background};
`;

const NewAddrActions = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing(1)};

  button {
    padding: ${p => p.theme.spacing(1.5)} ${p => p.theme.spacing(2.5)};
    border-radius: ${p => p.theme.borderRadius};
    font-size: ${p => p.theme.typography.fontSize.small};
    font-weight: ${p => p.theme.typography.fontWeight.medium};
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
    border: none;
  }

  .save-btn {
    background: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.black};
    &:hover { opacity: 0.9; transform: translateY(-1px); }
    &:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
  }

  .cancel-btn {
    background: ${p => p.theme.colors.surface};
    color: ${p => p.theme.colors.textSecondary};
    &:hover { color: ${p => p.theme.colors.textPrimary}; }
  }
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  border-bottom: 1px solid ${(props) => props.theme.colors.inputGray};
  margin-bottom: ${(props) => props.theme.spacing(2)};

  .header {
    display: flex;
    justify-content: space-between; /* Add space between supplier name and distance */
    width: 100%; /* Ensure the header takes full width */
    align-items: center;
  }

  .item-name {
    font-size: 18px;
    font-weight: bold;
  }
  
  .right-item {
    display: flex;
    font-size: 16px;
    font-weight: bold;
    color: ${(props) => props.theme.colors.primaryText};
  }

  .item-list {
    margin-top: ${(props) => props.theme.spacing(1)};
    font-size: 14px;
    color: ${(props) => props.theme.colors.textSecondary};
  }
`;

const ItemListTitle = styled.h3`
  font-size: 20px;
  color: ${(props) => props.theme.colors.primaryText};
  margin-bottom: ${(props) => props.theme.spacing(3)};
`;

const HeaderFlex = styled.div`
  display: flex !important;
  align-items: center;
  gap: ${props => props.theme.spacing(3)};
  justify-content: space-between !important;

`;

const StatusSelect = styled(Select)`
  min-width: 140px;
  padding: 10px !important;
  margin: 0px;
  font-size: 0.9em !important;
  border-color: ${props => props.statuscolor || props.theme.colors.inputGray};

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const PIPELINE_STAGES = [
  { key: 'new', label: 'New', color: '#4FC3F7' },
  { key: 'first_contact', label: 'First Contact', color: '#81C784' },
  { key: 'quote_sent', label: 'Quote Sent', color: '#FFB74D' },
  { key: 'quote_opened', label: 'Quote Opened', color: '#FF8A65' },
  { key: 'follow_up_1', label: '1st Follow Up', color: '#BA68C8' },
  { key: 'follow_up_2', label: '2nd Follow Up', color: '#9575CD' },
  { key: 'follow_up_3', label: '3rd Follow Up', color: '#7986CB' },
  { key: 'won', label: 'Won', color: '#66BB6A' },
  { key: 'lost', label: 'Lost', color: '#ef5350' },
];

const PipelineSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PipelineDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.color};
  flex-shrink: 0;
`;

const PipelineSelect = styled.select`
  padding: 8px 12px;
  background: ${p => p.stagecolor}15;
  color: ${p => p.theme.colors.textPrimary};
  border: 2px solid ${p => p.stagecolor || p.theme.colors.inputGray};
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  min-width: 140px;
  transition: all 0.2s;
  appearance: auto;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${p => p.stagecolor}33;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing(2)};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const StatusLabel = styled.span`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 600;
`;

const StatusGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ActionBar = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: stretch;
    button {
      flex: 1;
    }
  }
`;

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1.5)} ${props => props.theme.spacing(3)};
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  background: ${props => props.variant === 'primary' 
    ? props.theme.colors.primary 
    : props.variant === 'danger'
    ? props.theme.colors.error
    : props.theme.colors.surface};
  color: ${props => props.variant === 'primary' || props.variant === 'danger'
    ? props.theme.colors.black
    : props.theme.colors.textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${props => props.theme.typography.fontSize.small};
  font-weight: ${props => props.theme.typography.fontWeight.normal};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 1em;
    margin-right: 2px;
  }
`;

// Notes styled components
// LeadLogs mix three things in one feed: manual notes (the free text a rep
// types goes in `action`), lead activity (created/updated/pipeline), and a copy
// of every communication. The Communications tab already covers the last group,
// so the Notes tab can filter it out.
//
// Matched by shape rather than an exact list: logCommunication() writes
// `${action} Communication`, so the channel name varies.
const isCommunicationLog = (note) => {
  const a = note?.action || '';
  return /communication/i.test(a)          // "SMS Communication", "Communications Linked"
    || /^email sent$/i.test(a)             // CRM-sent email
    || /^ai agent call /i.test(a);         // AI call summary / transcript
};

const NoteFilterRow = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin-bottom: ${(props) => props.theme.spacing(2)};
`;

const NoteFilterBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 20px; cursor: pointer; font-size: 0.8rem;
  background: ${(p) => (p.$active ? p.theme.colors.primary : 'transparent')};
  color: ${(p) => (p.$active ? '#000' : p.theme.colors.textSecondary)};
  border: 1px solid ${(p) => (p.$active ? p.theme.colors.primary : p.theme.colors.inputGray + '55')};
  font-weight: ${(p) => (p.$active ? 700 : 500)};

  &:hover { border-color: ${(p) => p.theme.colors.primary}; }
`;

const NoteCard = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;
  border-left: 3px solid ${props => props.isAI ? props.theme.colors.primary : '#4caf50'};
`;

const NoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const NoteAction = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.9rem;
`;

const NoteDate = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.8rem;
`;

const NoteBody = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.85rem;
  white-space: pre-wrap;
  line-height: 1.5;
`;

const NoteActions = styled.div`
  display: flex;
  gap: 6px;
`;

const NoteIconBtn = styled.button`
  background: none;
  border: none;
  color: ${props => props.color || props.theme.colors.textSecondary};
  cursor: pointer;
  font-size: 0.85rem;
  padding: 2px;
  &:hover { color: ${props => props.theme.colors.primary}; }
`;

const NoteForm = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
`;

const NoteTextArea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.9rem;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  margin-bottom: 8px;
  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
`;

const NoteInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.9rem;
  box-sizing: border-box;
  margin-bottom: 8px;
  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
`;

const AddNoteBtn = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover { opacity: 0.9; }
`;

const NoteFormBtns = styled.div`
  display: flex;
  gap: 8px;
`;

const CancelBtn = styled.button`
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textSecondary};
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
`;

// Add a new styled component for the TabContainer
const TabContainer = styled.div`
  margin-top: ${props => props.theme.spacing(3)};
`;

const TabButtons = styled.div`
  display: flex;
  border-bottom: 1px solid ${props => props.theme.colors.inputGray}33;
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const TabButton = styled.button`
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(3)};
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textPrimary};
  font-weight: ${props => props.active ? props.theme.typography.fontWeight.bold : props.theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const formatActivityDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
};

const EstimateItemWithLog = ({ estimate, navigate, setSendEstimateId }) => {
  const [logOpen, setLogOpen] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(false);
  const items = estimate.items || [];

  const activities = [];

  // Created
  if (estimate.createdAt) {
    activities.push({
      icon: <FaPlus />,
      iconBg: '#4FC3F722',
      iconColor: '#4FC3F7',
      text: 'Created',
      date: estimate.createdAt,
    });
  }

  // Sent — use updatedAt when status >= Sent and createdAt differs, or estimate status history
  if (['Sent', 'Viewed', 'Accepted', 'Declined', 'Expired', 'Completed'].includes(estimate.status)) {
    activities.push({
      icon: <FaPaperPlane />,
      iconBg: '#FFB74D22',
      iconColor: '#FFB74D',
      text: 'Sent',
      date: estimate.updatedAt !== estimate.createdAt ? estimate.updatedAt : estimate.createdAt,
    });
  }

  // Views
  if (estimate.views && estimate.views.length > 0) {
    estimate.views.forEach((view, i) => {
      activities.push({
        icon: <FaEye />,
        iconBg: '#BA68C822',
        iconColor: '#BA68C8',
        text: `Viewed${estimate.views.length > 1 ? ` (#${i + 1})` : ''}`,
        date: view.viewedAt,
      });
    });
  }

  // Signature / Accepted
  if (estimate.signature) {
    activities.push({
      icon: <FaSignature />,
      iconBg: '#66BB6A22',
      iconColor: '#66BB6A',
      text: `Accepted by ${estimate.signature.fullName || 'customer'}`,
      date: estimate.signature.signedAt,
    });
  }

  // Declined
  if (estimate.status === 'Declined') {
    activities.push({
      icon: <FaTimesCircle />,
      iconBg: '#ef535022',
      iconColor: '#ef5350',
      text: 'Declined',
      date: estimate.updatedAt,
    });
  }

  // Sort by date ascending
  activities.sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <ItemListLink>
      <div className='header'>
        <span className='item-name'>
          Estimate #{estimate.id}
          <ChangeOrderSummaryBadge changeOrders={estimate.changeOrders} />
        </span>
        <div className="price">{formatCurrency(estimate.totalPrice)}</div>
        <StatusBadge color={getStatusColor(estimate.status)}>
          {estimate.status}
        </StatusBadge>
      </div>
      {estimate.leadAddress && (
        <EstimateAddrTag>
          <FaMapMarkerAlt size={10} />
          {estimate.leadAddress.label ? `${estimate.leadAddress.label} — ` : ''}
          {formatAddress(estimate.leadAddress)}
        </EstimateAddrTag>
      )}
      <div className="actions">
        {estimate.status === "Draft" && (
          <ActionButton onClick={() => navigate(`/estimates/edit/${estimate.id}`)}>
            <FaPen /> Edit
          </ActionButton>
        )}
        <ActionButton onClick={() => navigate(`/estimates/view/${estimate.id}`)}>
          <FaEye /> View
        </ActionButton>
        {['Draft', 'Sent', 'Viewed'].includes(estimate.status) && (
          <ActionButton onClick={() => setSendEstimateId(estimate.id)}>
            <FaPaperPlane /> Send
          </ActionButton>
        )}
      </div>
      {items.length > 0 && (
        <>
          <EstimateActivityToggle onClick={(e) => { e.stopPropagation(); setItemsOpen(!itemsOpen); }}>
            <FaListUl size={11} />
            {itemsOpen ? 'Hide' : 'Show'} items ({items.length})
            {itemsOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
          </EstimateActivityToggle>
          {itemsOpen && (
            <ItemPreview>
              {items.map((item) => (
                <ItemRow key={item.id}>
                  <span className="svc">{item.Service?.service_name || 'Service'}</span>
                  <span className="qty">{formatQty(item)}</span>
                  <span className="amt">{formatCurrency(item.totalPrice)}</span>
                </ItemRow>
              ))}
            </ItemPreview>
          )}
        </>
      )}

      {activities.length > 0 && (
        <>
          <EstimateActivityToggle onClick={(e) => { e.stopPropagation(); setLogOpen(!logOpen); }}>
            <FaClock size={11} />
            {logOpen ? 'Hide' : 'Show'} activity ({activities.length})
            {logOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
          </EstimateActivityToggle>
          {logOpen && (
            <ActivityLog>
              {activities.map((a, i) => (
                <ActivityItem key={i}>
                  <span className="activity-icon" style={{ background: a.iconBg, color: a.iconColor }}>
                    {a.icon}
                  </span>
                  <span className="activity-text">{a.text}</span>
                  <span className="activity-date">{formatActivityDate(a.date)}</span>
                </ActivityItem>
              ))}
            </ActivityLog>
          )}
        </>
      )}
    </ItemListLink>
  );
};

const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [futureJobs, setFutureJobs] = useState([]);
  const [pastJobs, setPastJobs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'communications', 'notes'
  const [notes, setNotes] = useState([]);
  const [showNoteForm, setShowNoteForm] = useState(false);
  // 'notes' hides the communication entries the Communications tab already shows.
  const [noteFilter, setNoteFilter] = useState('notes');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteForm, setNoteForm] = useState({ action: '', description: '' });
  const [showCallModal, setShowCallModal] = useState(false);
  const { user } = useAuth();  // Get current user
  const userId = user?.id;  // This is the user ID needed for SMS
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [sendEstimateId, setSendEstimateId] = useState(null);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [pipelineUpdating, setPipelineUpdating] = useState(false);
  const [leadAddresses, setLeadAddresses] = useState([]);
  // Which address pin the map should focus: { id, n } where id is null for the
  // primary address. The nonce makes clicking the same card twice re-focus it.
  const [focusedAddr, setFocusedAddr] = useState(null);
  const focusAddress = (id) => setFocusedAddr({ id, n: Date.now() });

  // Notes tab: split the LeadLog feed into "notes & lead updates" vs everything.
  const visibleNotes = useMemo(
    () => (noteFilter === 'all' ? notes : notes.filter(n => !isCommunicationLog(n))),
    [notes, noteFilter]
  );
  const noteCounts = useMemo(() => ({
    all: notes.length,
    notes: notes.filter(n => !isCommunicationLog(n)).length,
  }), [notes]);
  const [addrModalOpen, setAddrModalOpen] = useState(false); // create/edit modal
  const [addrModalMode, setAddrModalMode] = useState('create'); // 'create' | 'edit'
  const [editingAddr, setEditingAddr] = useState(null); // address being edited
  const [addrForm, setAddrForm] = useState({ label: '', street: '', city: '', state: '', zipcode: '', latitude: null, longitude: null });
  const [deleteAddrModal, setDeleteAddrModal] = useState(null); // address to delete (or null)
  const [directCallKey, setDirectCallKey] = useState(null);
  const [leadPhones, setLeadPhones] = useState([]); // [{ id, phone_number, label, is_primary }]
  const [leadEmails, setLeadEmails] = useState([]); // [{ id, email_address, label, is_primary }]
  const [emailModalOpen, setEmailModalOpen] = useState(false); // create/edit modal
  const [emailModalMode, setEmailModalMode] = useState('create'); // 'create' | 'edit'
  const [editingEmail, setEditingEmail] = useState(null); // row being edited
  const [emailForm, setEmailForm] = useState({ email_address: '', label: '' });
  const [emailFormError, setEmailFormError] = useState(null);
  const [deleteEmailModal, setDeleteEmailModal] = useState(null); // row to delete (or null)
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [reloadToken, setReloadToken] = useState(0); // bump to re-run the lead fetch
  const [commRefreshToken, setCommRefreshToken] = useState(0); // bump to remount CommunicationHistory
  const [submissions, setSubmissions] = useState([]); // website calculator submissions (with images)
  const [viewImage, setViewImage] = useState(null); // image open in the full-size viewer

  // Loaded on its own so a failure here never blocks the lead page.
  useEffect(() => {
    let cancelled = false;
    getLeadSubmissions(id)
      .then((data) => { if (!cancelled) setSubmissions(data.submissions || []); })
      .catch((err) => { console.error('Error fetching lead submissions:', err); if (!cancelled) setSubmissions([]); });
    return () => { cancelled = true; };
  }, [id, reloadToken]);

  // Outline snapshot per service request, for the thumbnail on its card.
  const imageByServiceRequest = useMemo(() => {
    const map = {};
    submissions.forEach((s) => {
      const img = (s.images || [])[0];
      if (img && s.LeadServiceRequestId && !map[s.LeadServiceRequestId]) map[s.LeadServiceRequestId] = img;
    });
    return map;
  }, [submissions]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [leadRes, jobsRes, estimatesRes, statusesRes] = await Promise.all([
          getLeadWithRelated(id),
          getLeadJobs(id),
          getLeadEstimates(id),
          getLeadStatuses()
        ]);

        setLead(leadRes.data);
        setLeadStatuses(statusesRes.data || []);
        const allJobs = jobsRes.data || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setFutureJobs(allJobs.filter(j => new Date(j.scheduled_start_date) >= today));
        setPastJobs(allJobs.filter(j => new Date(j.scheduled_start_date) < today).reverse());
        setEstimates(estimatesRes.data);
        // Fetch lead addresses (from related data or separate call)
        if (leadRes.data.addresses) {
          setLeadAddresses(leadRes.data.addresses);
        } else {
          try {
            const addrRes = await getLeadAddresses(id);
            setLeadAddresses(addrRes.data || []);
          } catch (e) { console.error('Error fetching addresses:', e); }
        }
        // Fetch lead phone numbers (from related data or separate call)
        if (leadRes.data.phoneNumbers) {
          setLeadPhones(leadRes.data.phoneNumbers);
        } else {
          try {
            const phoneRes = await getLeadPhoneNumbers(id);
            setLeadPhones(phoneRes.data || []);
          } catch (e) { console.error('Error fetching phone numbers:', e); }
        }
        // Fetch lead email addresses (from related data or separate call).
        // A cached pre-deploy payload has no `emailAddresses` key at all, and
        // the endpoint is a cache miss offline until a sync pass warms it —
        // both fall through to an empty list, which renders as today.
        // Sort defensively: the primary must come first (it seeds the
        // send-quote picker), and a cached payload may predate the ordering.
        const byPrimaryFirst = (rows) =>
          [...(rows || [])].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
        if (leadRes.data.emailAddresses) {
          setLeadEmails(byPrimaryFirst(leadRes.data.emailAddresses));
        } else {
          try {
            const emailRes = await getLeadEmails(id);
            setLeadEmails(byPrimaryFirst(emailRes.data));
          } catch (e) { console.error('Error fetching email addresses:', e); }
        }
      } catch (error) {
        console.error('Error fetching lead details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, reloadToken]);

  // Load notes when tab switches to notes
  useEffect(() => {
    if (activeTab === 'notes' && id) {
      getLeadNotes(id).then(setNotes).catch(err => console.error('Error loading notes:', err));
    }
  }, [activeTab, id]);

  // Pre-fetch direct-call key so the Share Lead click handler can write to
  // the clipboard synchronously (Safari/strict-Chrome drop the user-gesture
  // permission across an awaited fetch, blocking clipboard.writeText).
  useEffect(() => {
    getDirectCallKey()
      .then(({ key }) => setDirectCallKey(key))
      .catch(err => console.error('Failed to prefetch direct-call key:', err));
  }, []);

  const handleSaveNote = async () => {
    try {
      if (editingNoteId) {
        await updateLeadNote(editingNoteId, noteForm);
      } else {
        await createLeadNote({ leadId: id, action: noteForm.action || 'Note', description: noteForm.description, userId });
      }
      const updated = await getLeadNotes(id);
      setNotes(updated);
      setShowNoteForm(false);
      setEditingNoteId(null);
      setNoteForm({ action: '', description: '' });
    } catch (err) {
      console.error('Error saving note:', err);
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setNoteForm({ action: note.action, description: note.description });
    setShowNoteForm(true);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await deleteLeadNote(noteId);
      setNotes(notes.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!lead) return <div>Lead not found</div>;

  // Modify the handleCall function to use the CallModal
  const handleCall = () => {
    setCallModalOpen(true);
  };

  // NEW: Handle successful call initiation
  const handleCallSuccess = (callResult) => {
    console.log('Call initiated successfully:', callResult);
    // Optionally show a success notification here
    // You can also refresh the lead data if needed
  };

  // `recipientArg` is passed explicitly by the picker; reading it from state
  // here would see the value from before the click.
  const handleSendEstimate = async (recipientArg) => {
    const estId = sendEstimateId;
    const recipient = typeof recipientArg === 'string' ? recipientArg : null;
    setSendEstimateId(null);
    try {
      setLoading(true);
      // Omit to_email entirely when there is nothing to choose, so the request
      // is byte-identical to the pre-picker one.
      const payload = recipient ? { newStatus: 'Sent', to_email: recipient } : { newStatus: 'Sent' };
      const response = await updateEstimateStatus(estId, payload);
      if (response?.status === 200) {
        setEstimates(prev => prev.map(e => e.id === estId ? { ...e, status: 'Sent' } : e));
        setFeedbackMessage(`Estimate sent to ${recipient || lead?.email}! Quote link delivered via email and SMS.`);
        setFeedbackSuccess(true);
      } else {
        // updateEstimateStatus swallows its error and returns undefined, so
        // without this the modal would show whatever feedback was set last.
        setFeedbackMessage('Error sending estimate. Please try again.');
        setFeedbackSuccess(false);
      }
    } catch (error) {
      setFeedbackMessage(error.message || 'Error sending estimate');
      setFeedbackSuccess(false);
    } finally {
      setLoading(false);
      setShowFeedback(true);
    }
  };

  const handleCopyField = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      // silent fail
    }
  };

  const handleShareLead = () => {
    const fullName = lead.name || '';
    const fullAddress = [lead.street, lead.city, lead.state, lead.zipcode].filter(Boolean).join(', ');
    const digits = (lead.phone || '').replace(/\D/g, '');
    const formattedPhone = digits.length === 10
      ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      : digits.length === 11 && digits.startsWith('1')
        ? `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
        : (lead.phone || '');
    const leadUrl = `${window.location.origin}/leads/view/${lead.id}`;

    // `from` left empty — user edits in their phone after pasting.
    const directCallUrl = directCallKey
      ? `https://crm.mrpatchasphalt.com/direct-call?to=${encodeURIComponent(formatPhoneNumber(lead.phone))}&leadId=${encodeURIComponent(lead.id)}&k=${encodeURIComponent(directCallKey)}&from=`
      : '';

    const text = [fullName, fullAddress, formattedPhone, leadUrl, directCallUrl]
      .filter(Boolean)
      .join('\n');

    const showCopied = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    const fallbackCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try { ok = document.execCommand('copy'); } catch { ok = false; }
      document.body.removeChild(ta);
      if (ok) {
        showCopied();
      } else {
        setFeedbackMessage('Failed to copy to clipboard');
        setFeedbackSuccess(false);
        setShowFeedback(true);
      }
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(showCopied).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };

  const currentStatusName = lead.status?.status_name || lead.serviceRequests?.[0]?.LeadStatus?.status_name || 'New';

  const handleStatusChange = async (e) => {
    const newStatusId = e.target.value;
    if (!newStatusId) return;

    try {
      setStatusUpdating(true);
      const response = await updateLeadStatus(lead.id, newStatusId);

      if (response.status === 200) {
        // Refresh lead data to get updated status
        const leadRes = await getLeadWithRelated(id);
        setLead(leadRes.data);
        setFeedbackMessage(`Status updated to ${response.data.status.status_name}`);
        setFeedbackSuccess(true);
      }
    } catch (error) {
      setFeedbackMessage(error.message || 'Error updating status');
      setFeedbackSuccess(false);
    } finally {
      setStatusUpdating(false);
      setShowFeedback(true);
    }
  };

  const currentPipelineStage = lead.pipeline_stage || 'new';
  const currentPipelineConfig = PIPELINE_STAGES.find(s => s.key === currentPipelineStage) || PIPELINE_STAGES[0];

  const handlePipelineChange = async (e) => {
    const newStage = e.target.value;
    if (!newStage || newStage === currentPipelineStage) return;

    try {
      setPipelineUpdating(true);
      await updateLeadPipeline(lead.id, newStage);
      const stageLabel = PIPELINE_STAGES.find(s => s.key === newStage)?.label || newStage;

      // Refresh lead data
      const leadRes = await getLeadWithRelated(id);
      setLead(leadRes.data);

      setFeedbackMessage(`Pipeline updated to ${stageLabel}`);
      setFeedbackSuccess(true);
    } catch (error) {
      setFeedbackMessage(error.message || 'Error updating pipeline');
      setFeedbackSuccess(false);
    } finally {
      setPipelineUpdating(false);
      setShowFeedback(true);
    }
  };

  return (
    <DashboardLayout>
        <DetailHeader>
        <HeaderFlex>
          <DetailTitle>
            <FaUser size={24} />
            <h1>{lead.name}</h1>
            {lead.origin?.origin_name && (
              <OriginBadge origin={lead.origin} />
            )}
          </DetailTitle>
          <StatusRow>
            <StatusGroup>
              <StatusLabel>Status</StatusLabel>
              <StatusSelect
                value={lead.LeadStatusId || lead.serviceRequests?.[0]?.LeadStatusId || ''}
                onChange={handleStatusChange}
                statuscolor={lead.status?.color || lead.serviceRequests?.[0]?.LeadStatus?.color || '#95a5a6'}
                label={"Status"}
                disabled={statusUpdating}
              >
                {leadStatuses.map(status => (
                  <option key={status.id} value={status.id}>{status.status_name}</option>
                ))}
              </StatusSelect>
            </StatusGroup>
            <StatusGroup>
              <StatusLabel>Pipeline</StatusLabel>
              <PipelineSelector>
                <PipelineDot color={currentPipelineConfig.color} />
                <PipelineSelect
                  value={currentPipelineStage}
                  onChange={handlePipelineChange}
                  stagecolor={currentPipelineConfig.color}
                  disabled={pipelineUpdating}
                >
                  {PIPELINE_STAGES.map(stage => (
                    <option key={stage.key} value={stage.key}>{stage.label}</option>
                  ))}
                </PipelineSelect>
              </PipelineSelector>
            </StatusGroup>
          </StatusRow>
        </HeaderFlex>
        <ActionBar>
            <ActionButton variant="success" onClick={() => navigate(-1)}>
              <FaArrowLeft /> Back
            </ActionButton>
            <ActionButton onClick={handleCall}>
              <FaPhoneAlt /> Call
            </ActionButton>
            <ActionButton onClick={() => setShowSmsModal(true)}>
              <FaSms /> Send SMS
            </ActionButton>
            <ActionButton onClick={() => setShowEmailModal(true)}>
              <FaEnvelope /> Send Email
            </ActionButton>
            <AddToRouteButton leadId={lead.id} onAdded={() => setReloadToken(t => t + 1)} />
            <ActionButton onClick={() => navigate(`/leads/edit/${id}`)}>
              <FaEdit /> Edit
            </ActionButton>
            {lead.street && lead.city && (
              <>
              <ActionButton onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${lead.street}, ${lead.city}, ${lead.state} ${lead.zipcode}`)}`, '_blank')}>
                <FaDirections /> Directions
              </ActionButton>
              {lead.latitude && lead.longitude && (
              <ActionButton onClick={() => {
                const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                window.open(`https://apps.nearmap.com/maps/#/@${lead.latitude},${lead.longitude},23.00z,0d/V/${today}`, '_blank');
              }}>
                <FaSatellite /> Nearmap View
              </ActionButton>
              )}
              </>
            )}
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <ActionButton onClick={handleShareLead}>
                <FaShareAlt /> {copied ? 'Copied!' : 'Share Lead'}
              </ActionButton>
              {copied && (
                <span style={{
                  position: 'absolute',
                  bottom: '110%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#27ae60',
                  color: '#fff',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '0.78rem',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                }}>
                  Copied to clipboard!
                </span>
              )}
            </div>
            <ActionButton onClick={() => setShowMergeModal(true)}>
              <FaLink /> Link / Merge Number
            </ActionButton>
            <ActionButton variant="primary" onClick={() => navigate(`/estimates/new/${lead.id}`)}>
              <FaFileInvoiceDollar /> Generate Quote
            </ActionButton>
          </ActionBar>
          <ContactInfoBar>
            {leadPhones.length > 0 ? (
              leadPhones.map((p, idx) => {
                const num = p.phone_number || p.phone;
                const field = `phone-${p.id ?? idx}`;
                return (
                  <CopyChip key={field} $copied={copiedField === field} onClick={() => handleCopyField(field, num)}>
                    <FaPhoneAlt className="chip-icon" />
                    {copiedField === field
                      ? 'Copied!'
                      : `${num}${p.label ? ` (${p.label})` : ''}`}
                    <FaCopy className="copy-icon" />
                  </CopyChip>
                );
              })
            ) : lead.phone && (
              <CopyChip $copied={copiedField === 'phone'} onClick={() => handleCopyField('phone', lead.phone)}>
                <FaPhoneAlt className="chip-icon" />
                {copiedField === 'phone' ? 'Copied!' : lead.phone}
                <FaCopy className="copy-icon" />
              </CopyChip>
            )}
            {leadEmails.length > 0 ? (
              leadEmails.map((em, idx) => {
                const addr = em.email_address;
                const field = `email-${em.id ?? idx}`;
                return (
                  <CopyChip key={field} $copied={copiedField === field} onClick={() => handleCopyField(field, addr)}>
                    <FaEnvelope className="chip-icon" />
                    {copiedField === field
                      ? 'Copied!'
                      : `${addr}${em.label && !em.is_primary ? ` (${em.label})` : ''}`}
                    <FaCopy className="copy-icon" />
                  </CopyChip>
                );
              })
            ) : lead.email && (
              <CopyChip $copied={copiedField === 'email'} onClick={() => handleCopyField('email', lead.email)}>
                <FaEnvelope className="chip-icon" />
                {copiedField === 'email' ? 'Copied!' : lead.email}
                <FaCopy className="copy-icon" />
              </CopyChip>
            )}
            {lead.street && (
              <CopyChip $copied={copiedField === 'address'} onClick={() => handleCopyField('address', [lead.street, lead.city, lead.state, lead.zipcode].filter(Boolean).join(', '))}>
                <FaMapMarkerAlt className="chip-icon" />
                {copiedField === 'address' ? 'Copied!' : [lead.street, lead.city, lead.state, lead.zipcode].filter(Boolean).join(', ')}
                <FaCopy className="copy-icon" />
              </CopyChip>
            )}
            {lead.origin?.origin_name && (
              <OriginBadge origin={lead.origin} />
            )}
            <MetaTag color={lead.accept_sms ? '#84c959' : '#FF5252'}>
              {lead.accept_sms ? <FaCheckCircle /> : <FaTimesCircle />}
              {lead.accept_sms ? 'Accepts SMS' : 'No SMS'}
            </MetaTag>
            <PreferredContactBadge value={lead.preferred_contact} />
          </ContactInfoBar>
        </DetailHeader>

        {/* Add Tab Navigation */}
      <TabContainer>
        <TabButtons>
          <TabButton 
            active={activeTab === 'details'} 
            onClick={() => setActiveTab('details')}
          >
            <FaUser /> Details
          </TabButton>
          <TabButton 
            active={activeTab === 'communications'} 
            onClick={() => setActiveTab('communications')}
          >
            <FaHistory /> Communication History
          </TabButton>
          <TabButton
            active={activeTab === 'notes'}
            onClick={() => setActiveTab('notes')}
          >
            <FaComment /> Notes ({visibleNotes.length})
          </TabButton>
        </TabButtons>

        {activeTab === 'details' ? (
          <></>
        ) : activeTab === 'communications' ? (
          <DetailSection>
            <CommunicationHistory key={commRefreshToken} leadId={id} />
          </DetailSection>
        ) : (
          <DetailSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}><FaComment style={{ marginRight: '8px' }} />Notes</h3>
              <AddNoteBtn onClick={() => { setEditingNoteId(null); setNoteForm({ action: '', description: '' }); setShowNoteForm(true); }}>
                <FaPlus /> Add Note
              </AddNoteBtn>
            </div>

            <NoteFilterRow>
              <NoteFilterBtn $active={noteFilter === 'notes'} onClick={() => setNoteFilter('notes')}>
                <FaComment size={11} /> Notes &amp; updates ({noteCounts.notes})
              </NoteFilterBtn>
              <NoteFilterBtn $active={noteFilter === 'all'} onClick={() => setNoteFilter('all')}>
                Everything ({noteCounts.all})
              </NoteFilterBtn>
              {noteFilter === 'notes' && noteCounts.all > noteCounts.notes && (
                <span style={{ color: '#999', fontSize: '0.76rem' }}>
                  {noteCounts.all - noteCounts.notes} communication entries hidden — see the Communications tab
                </span>
              )}
            </NoteFilterRow>

            {showNoteForm && (
              <NoteForm>
                <NoteInput
                  placeholder="Title (e.g. Follow-up, Callback, Note...)"
                  value={noteForm.action}
                  onChange={e => setNoteForm(prev => ({ ...prev, action: e.target.value }))}
                />
                <NoteTextArea
                  placeholder="Write your note here..."
                  value={noteForm.description}
                  onChange={e => setNoteForm(prev => ({ ...prev, description: e.target.value }))}
                />
                <NoteFormBtns>
                  <AddNoteBtn onClick={handleSaveNote}><FaSave /> Save</AddNoteBtn>
                  <CancelBtn onClick={() => { setShowNoteForm(false); setEditingNoteId(null); }}>Cancel</CancelBtn>
                </NoteFormBtns>
              </NoteForm>
            )}

            {visibleNotes.length === 0 && !showNoteForm ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                {notes.length === 0
                  ? 'No notes yet.'
                  : 'No notes or lead updates — only communication entries, which are on the Communications tab.'}
              </div>
            ) : (
              visibleNotes.map(note => (
                <NoteCard key={note.id} isAI={note.action && note.action.includes('AI')}>
                  <NoteHeader>
                    <div>
                      <NoteAction>{note.action}</NoteAction>
                      {note.User && <span style={{ color: '#999', fontSize: '0.8rem', marginLeft: '8px' }}>by {note.User.username}</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <NoteDate>{new Date(note.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</NoteDate>
                      <NoteActions>
                        <NoteIconBtn onClick={() => handleEditNote(note)}><FaEdit /></NoteIconBtn>
                        <NoteIconBtn color="#fa7f7f" onClick={() => handleDeleteNote(note.id)}><FaTrash /></NoteIconBtn>
                      </NoteActions>
                    </div>
                  </NoteHeader>
                  <NoteBody>{note.description}</NoteBody>
                </NoteCard>
              ))
            )}
          </DetailSection>
        )}
      </TabContainer>

        {lead.latitude && lead.longitude && (
          <WeatherForecast
            latitude={parseFloat(lead.latitude)}
            longitude={parseFloat(lead.longitude)}
            title={`Weather at ${lead.street || 'Job Site'}`}
          />
        )}

        {/* Scheduled visits (routes) and scheduled calls — paired on desktop */}
        <SectionRow>
          <LeadScheduledVisits leadId={lead.id} />
          <LeadScheduledCalls lead={lead} />
        </SectionRow>

        {/* Addresses and email addresses — paired on desktop */}
        <SectionRow>
          <CollapsibleSection id="addresses" title="Addresses" icon={<FaMapMarkerAlt />} count={1 + leadAddresses.length}>
            <AddressCard
              $primary
              onClick={() => focusAddress(null)}
              style={{ cursor: 'pointer' }}
              title="Show on map"
            >
              <span className="addr-badge">Primary</span>
              <span className="addr-text">{[lead.street, lead.city, lead.state, lead.zipcode].filter(Boolean).join(', ')}</span>
            </AddressCard>
            {leadAddresses.map((addr) => (
              <AddressCard
                key={addr.id}
                onClick={() => focusAddress(addr.id)}
                style={{ cursor: 'pointer' }}
                title="Show on map"
              >
                {addr.label && <span className="addr-label">{addr.label}</span>}
                <span className="addr-text">{[addr.street, addr.city, addr.state, addr.zipcode].filter(Boolean).join(', ')}</span>
                <AddrActions>
                  <button
                    className="addr-action-btn"
                    title="Edit address"
                    onClick={(e) => {
                      e.stopPropagation(); // don't also trigger the card's focus-on-map
                      setAddrModalMode('edit');
                      setEditingAddr(addr);
                      setAddrForm({ label: addr.label || '', street: addr.street, city: addr.city, state: addr.state, zipcode: addr.zipcode, latitude: addr.latitude, longitude: addr.longitude });
                      setAddrModalOpen(true);
                    }}
                  ><FaEdit /></button>
                  <button
                    className="addr-action-btn delete"
                    title="Delete address"
                    onClick={(e) => { e.stopPropagation(); setDeleteAddrModal(addr); }}
                  ><FaTrash /></button>
                </AddrActions>
              </AddressCard>
            ))}
            <AddAddressBtn onClick={() => {
              setAddrModalMode('create');
              setEditingAddr(null);
              setAddrForm({ label: '', street: '', city: '', state: '', zipcode: '', latitude: null, longitude: null });
              setAddrModalOpen(true);
            }}>
              <FaPlus size={10} /> Add Address
            </AddAddressBtn>

            {/* Pins the primary address plus any additional ones that have
                coordinates. Clicking a card above focuses its pin. */}
            <LeadAddressMap
              lead={lead}
              addresses={leadAddresses}
              focusTarget={focusedAddr}
            />
          </CollapsibleSection>

          {/* Email Addresses Section — corporate leads often have several
              contacts. Every address here can open and sign the public quote. */}
          <CollapsibleSection id="emails" title="Email Addresses" icon={<FaEnvelope />} count={leadEmails.length || (lead.email ? 1 : 0)}>
            {(leadEmails.length > 0
              ? leadEmails
              : (lead.email ? [{ id: 'primary-fallback', email_address: lead.email, is_primary: true, label: 'Primary' }] : [])
            ).map((em) => (
              <AddressCard key={em.id} $primary={em.is_primary || undefined}>
                {em.is_primary
                  ? <span className="addr-badge">Primary</span>
                  : em.label && <span className="addr-label">{em.label}</span>}
                <span className="addr-text">{em.email_address}</span>
                {!em.is_primary && (
                  <AddrActions>
                    <button
                      className="addr-action-btn"
                      title="Edit email address"
                      onClick={() => {
                        setEmailModalMode('edit');
                        setEditingEmail(em);
                        setEmailForm({ email_address: em.email_address, label: em.label || '' });
                        setEmailFormError(null);
                        setEmailModalOpen(true);
                      }}
                    ><FaEdit /></button>
                    <button
                      className="addr-action-btn delete"
                      title="Delete email address"
                      onClick={() => setDeleteEmailModal(em)}
                    ><FaTrash /></button>
                  </AddrActions>
                )}
              </AddressCard>
            ))}
            <AddAddressBtn onClick={() => {
              setEmailModalMode('create');
              setEditingEmail(null);
              setEmailForm({ email_address: '', label: '' });
              setEmailFormError(null);
              setEmailModalOpen(true);
            }}>
              <FaPlus size={10} /> Add Email
            </AddAddressBtn>
            <Hint>
              The primary address is the one on the lead record — edit the lead to change it.
              Any address listed here can open and sign this lead's quotes.
            </Hint>
          </CollapsibleSection>
        </SectionRow>

        <CollapsibleSection id="services" title="Services Request" icon={<FaTractor />} count={lead.serviceRequests.length}>
            {lead.serviceRequests.map(serviceRequest => (
              <ItemListLink key={serviceRequest.id}>
              <div className='header'>
              <span className='item-name'>{serviceRequest.Service.service_name}</span>
              <StatusBadge color={getStatusColor(serviceRequest.LeadStatus.status_name)}>
                {serviceRequest.LeadStatus.status_name}
              </StatusBadge>
              </div>
              <div className='item-list-icon' style={{ whiteSpace: 'pre-wrap' }}>
                {serviceRequest.job_information}
              </div>
              {serviceRequest.job_observation && (
                <div style={{
                  marginTop: '8px',
                  padding: '10px 12px',
                  background: '#fff8e1',
                  border: '1px solid #ffe8a3',
                  borderLeft: '3px solid #ffb800',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#3a3a3a',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.5
                }}>
                  <strong style={{ display: 'block', fontSize: '11px', letterSpacing: '.08em', textTransform: 'uppercase', color: '#8a6500', marginBottom: '4px' }}>
                    Customer notes
                  </strong>
                  {serviceRequest.job_observation}
                </div>
              )}
              <SubmissionThumb image={imageByServiceRequest[serviceRequest.id]} onOpen={setViewImage} />
              {!serviceRequest.EstimateId &&
              <div className="actions">
              <ActionButton onClick={() => navigate(`/estimates/new/${lead.id}?serviceRequestIds=${serviceRequest.id}`)}>
                    <FaPlus /> New Estimate
                  </ActionButton>
              </div>
              }
              </ItemListLink>
              ))}
        </CollapsibleSection>

        <LeadSubmissions submissions={submissions} onOpenImage={setViewImage} />
        <ImageViewer image={viewImage} onClose={() => setViewImage(null)} />

        {(lead.job_observation || lead.preferred_contact) && (
          <CollapsibleSection id="observation" title="Lead Observation" icon={<FaComment />}>
            {lead.preferred_contact && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
                marginBottom: lead.job_observation ? '12px' : 0,
                fontSize: '14px',
                color: '#2a2a2a'
              }}>
                <strong>Preferred contact:</strong>
                {preferredContactLabel(lead.preferred_contact)}
                <PreferredContactBadge value={lead.preferred_contact} size="sm" />
              </div>
            )}
            {lead.job_observation && (
              <div style={{
                padding: '14px 16px',
                background: '#fff8e1',
                border: '1px solid #ffe8a3',
                borderLeft: '4px solid #ffb800',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#2a2a2a',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.55
              }}>
                {lead.job_observation}
              </div>
            )}
          </CollapsibleSection>
        )}
        
        <CollapsibleSection id="estimates" title="Estimates" icon={<FaList />} count={estimates.length}>
          {estimates.map(estimate => (
            <EstimateItemWithLog key={estimate.id} estimate={estimate} navigate={navigate} setSendEstimateId={setSendEstimateId} />
          ))}
        </CollapsibleSection>

        <CollapsibleSection id="upcomingJobs" title="Upcoming Jobs" icon={<FaCalendarAlt />} count={futureJobs.length}>
          {futureJobs.length === 0 ? (
            <ItemList><div className='item-list' style={{ textAlign: 'center', padding: '16px' }}>No upcoming jobs</div></ItemList>
          ) : (
            futureJobs.map((job) => (
              <ItemListLink key={job.id} onClick={() => navigate(`/jobs/${job.job_id}`)}>
                <div className='header'>
                  <span className='item-name'>
                    {formatDate(job.scheduled_start_date)}
                    {job.scheduled_end_date !== job.scheduled_start_date ? ` to ${formatDate(job.scheduled_end_date)}` : ''}
                  </span>
                  <StatusBadge color={getStatusColor(job.status)}>
                    {job.status}
                  </StatusBadge>
                </div>
                <div className='item-list'>
                  Estimate #{job.Estimate?.id} / {job.services.map((s, i) => (
                    <span key={i}>{s.service_name}{i < job.services.length - 1 ? ', ' : ''}</span>
                  ))}
                </div>
              </ItemListLink>
            ))
          )}
        </CollapsibleSection>

        <CollapsibleSection id="pastJobs" title="Past Jobs" icon={<FaHistory />} count={pastJobs.length}>
          {pastJobs.length === 0 ? (
            <ItemList><div className='item-list' style={{ textAlign: 'center', padding: '16px' }}>No past jobs</div></ItemList>
          ) : (
            pastJobs.map((job) => (
              <ItemListLink key={job.id} onClick={() => navigate(`/jobs/${job.job_id}`)}>
                <div className='header'>
                  <span className='item-name'>
                    {formatDate(job.scheduled_start_date)}
                    {job.scheduled_end_date !== job.scheduled_start_date ? ` to ${formatDate(job.scheduled_end_date)}` : ''}
                  </span>
                  <StatusBadge color={getStatusColor(job.status)}>
                    {job.status}
                  </StatusBadge>
                </div>
                <div className='item-list'>
                  Estimate #{job.Estimate?.id} / {job.services.map((s, i) => (
                    <span key={i}>{s.service_name}{i < job.services.length - 1 ? ', ' : ''}</span>
                  ))}
                </div>
              </ItemListLink>
            ))
          )}
        </CollapsibleSection>

        {showSmsModal && lead && (
          <SmsModal
            isOpen={showSmsModal}
            onClose={() => setShowSmsModal(false)}
            lead={{
              id: lead.id,
              name: lead.name,
              phone: lead.phone,  // Make sure this is a string
              city: lead.city,
              book_a_call_url: lead.book_a_call_url,  // {book_a_call_url} template var
              estimates  // powers the {quote_url} / {estimate_total} template vars
            }}
            userId={user?.id}  // If you have the user ID available
          />
        )}

        {showEmailModal && lead && (
          <EmailModal
            isOpen={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            lead={{
              id: lead.id,
              name: lead.name,
              email: lead.email,  // nullable — the modal blocks send when absent
            }}
            userId={user?.id}
          />
        )}

        <SendEstimateModal
          isOpen={!!sendEstimateId}
          estimateId={sendEstimateId}
          lead={lead}
          onConfirm={handleSendEstimate}
          onCancel={() => setSendEstimateId(null)}
        />

        {showMergeModal && lead && (
          <MergeLeadModal
            isOpen={showMergeModal}
            currentLead={{ id: lead.id, name: lead.name, phone: lead.phone }}
            onClose={() => setShowMergeModal(false)}
            onMerged={(updatedLead, sourceLead) => {
              // Refresh the lead (new number chip) and the communication history
              // (now includes the absorbed lead's calls/SMS).
              setReloadToken(t => t + 1);
              setCommRefreshToken(t => t + 1);
              setFeedbackMessage(
                `Merged ${sourceLead?.name || 'the duplicate lead'} into this lead. Its calls and messages are now here.`
              );
              setFeedbackSuccess(true);
              setShowFeedback(true);
            }}
          />
        )}

        <FeedbackModal
          isOpen={showFeedback}
          message={feedbackMessage}
          success={feedbackSuccess}
          onComplete={() => setShowFeedback(false)}
        />

        {/* CallModal for click-to-call functionality */}
        {callModalOpen && (
          <CallModal
            isOpen={callModalOpen}
            onClose={() => setCallModalOpen(false)}
            customerPhone={lead.phone}
            customerName={lead.name}
            leadId={lead.id}
            onCallSuccess={handleCallSuccess}
          />
        )}

        {/* Address Create/Edit Modal */}
        <Modal
          isOpen={addrModalOpen}
          title={addrModalMode === 'edit' ? 'Edit Address' : 'Add Address'}
          hideFooter
          onCancel={() => setAddrModalOpen(false)}
        >
          <AddrModalBody>
            <Input
              label="Label (optional)"
              value={addrForm.label}
              onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })}
              placeholder="e.g. Building A, Property #2"
            />
            <GooglePlaceAutocomplete
              key={addrModalMode + (editingAddr?.id || 'new')}
              initialValue={addrForm.street}
              placeholder="Enter address"
              onSelect={(address, lat, lng, place) => {
                setAddrForm({
                  ...addrForm,
                  street: address,
                  city: place.address_components.find((c) => c.types.includes('locality'))?.long_name || '',
                  state: place.address_components.find((c) => c.types.includes('administrative_area_level_1'))?.short_name || '',
                  zipcode: place.address_components.find((c) => c.types.includes('postal_code'))?.long_name || '',
                  latitude: lat,
                  longitude: lng,
                });
              }}
            />
            <NewAddrActions>
              <button
                className="save-btn"
                disabled={!addrForm.street}
                onClick={async () => {
                  if (!addrForm.street) return;
                  try {
                    if (addrModalMode === 'edit' && editingAddr) {
                      const res = await updateLeadAddress(lead.id, editingAddr.id, addrForm);
                      setLeadAddresses(prev => prev.map(a => a.id === editingAddr.id ? res.data : a));
                    } else {
                      const res = await createLeadAddress(lead.id, addrForm);
                      setLeadAddresses(prev => [...prev, res.data]);
                    }
                    setAddrModalOpen(false);
                    setAddrForm({ label: '', street: '', city: '', state: '', zipcode: '', latitude: null, longitude: null });
                  } catch (e) {
                    console.error('Error saving address:', e);
                    setFeedbackMessage('Error saving address. Please try again.');
                    setFeedbackSuccess(false);
                    setShowFeedback(true);
                  }
                }}
              >{addrModalMode === 'edit' ? 'Update' : 'Save'}</button>
              <button className="cancel-btn" onClick={() => setAddrModalOpen(false)}>Cancel</button>
            </NewAddrActions>
          </AddrModalBody>
        </Modal>

        {/* Address Delete Confirmation Modal */}
        <Modal
          isOpen={!!deleteAddrModal}
          title="Delete Address"
          message={`Delete "${deleteAddrModal?.label || deleteAddrModal?.street}"? Estimates using this address will show the main address instead.`}
          onConfirm={async () => {
            try {
              await deleteLeadAddress(lead.id, deleteAddrModal.id);
              setLeadAddresses(prev => prev.filter(a => a.id !== deleteAddrModal.id));
              setDeleteAddrModal(null);
            } catch (e) {
              console.error('Error deleting address:', e);
              setFeedbackMessage('Error deleting address.');
              setFeedbackSuccess(false);
              setShowFeedback(true);
              setDeleteAddrModal(null);
            }
          }}
          onCancel={() => setDeleteAddrModal(null)}
        />

        {/* Email Address Create/Edit Modal */}
        <Modal
          isOpen={emailModalOpen}
          title={emailModalMode === 'edit' ? 'Edit Email Address' : 'Add Email Address'}
          hideFooter
          onCancel={() => setEmailModalOpen(false)}
        >
          <AddrModalBody>
            <Input
              label="Email address"
              type="email"
              value={emailForm.email_address}
              onChange={(e) => setEmailForm({ ...emailForm, email_address: e.target.value })}
              placeholder="name@company.com"
            />
            <Input
              label="Label (optional)"
              value={emailForm.label}
              onChange={(e) => setEmailForm({ ...emailForm, label: e.target.value })}
              placeholder="e.g. Accounts Payable, Property Manager"
            />
            {emailFormError && <FieldError>{emailFormError}</FieldError>}
            <NewAddrActions>
              <button
                className="save-btn"
                disabled={!emailForm.email_address.includes('@')}
                onClick={async () => {
                  if (!emailForm.email_address.includes('@')) return;
                  setEmailFormError(null);
                  try {
                    if (emailModalMode === 'edit' && editingEmail) {
                      const res = await updateLeadEmail(lead.id, editingEmail.id, emailForm);
                      setLeadEmails(prev => prev.map(a => a.id === editingEmail.id ? res.data : a));
                    } else {
                      const res = await createLeadEmail(lead.id, emailForm);
                      setLeadEmails(prev => [...prev, res.data]);
                    }
                    setEmailModalOpen(false);
                    setEmailForm({ email_address: '', label: '' });
                  } catch (e) {
                    // Duplicates and "already the primary" come back as 409 with
                    // a useful message — show it in place rather than as a toast.
                    setEmailFormError(e.response?.data?.message || 'Error saving email address. Please try again.');
                  }
                }}
              >{emailModalMode === 'edit' ? 'Update' : 'Save'}</button>
              <button className="cancel-btn" onClick={() => setEmailModalOpen(false)}>Cancel</button>
            </NewAddrActions>
          </AddrModalBody>
        </Modal>

        {/* Email Address Delete Confirmation Modal */}
        <Modal
          isOpen={!!deleteEmailModal}
          title="Delete Email Address"
          message={`Remove "${deleteEmailModal?.email_address}" from this lead? They will no longer be able to open this lead's quotes.`}
          onConfirm={async () => {
            try {
              await deleteLeadEmail(lead.id, deleteEmailModal.id);
              setLeadEmails(prev => prev.filter(a => a.id !== deleteEmailModal.id));
              setDeleteEmailModal(null);
            } catch (e) {
              setFeedbackMessage(e.response?.data?.message || 'Error deleting email address.');
              setFeedbackSuccess(false);
              setShowFeedback(true);
              setDeleteEmailModal(null);
            }
          }}
          onCancel={() => setDeleteEmailModal(null)}
        />
    </DashboardLayout>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Draft': return '#f39c12';
    case 'Sent': return '#3498db';
    case 'Viewed': return '#2ecc71';
    case 'Accepted': return '#27ae60';
    case 'Declined': return '#e74c3c';
    case 'Canceled': return '#e74c3c';
    case 'Scheduled': return '#27ae60';
    case 'New': return '#3d5afe';
    case 'Prospect': return '#f39c12';
    case 'Estimate Sent': return '#3498db';
    case 'Converted': return '#27ae60';
    case 'Won': return '#9b59b6';
    case 'Customer': return '#28a745';
    case 'Lost': return '#e74c3c';
    case 'Imported': return '#9C27B0';
    default: return '#95a5a6';
  }
};

export default LeadDetails;